import { useState } from "react";
import { Briefcase, ShieldCheck, Users, Heart, TrendingUp, Send, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CTABand } from "../components/CTABand";
import { useSEO } from "../lib/useSEO";

const benefits = [
  { icon: ShieldCheck, title: "SIA Licensed Roles", desc: "All officers are properly licensed, trained and supported." },
  { icon: TrendingUp, title: "Career Progression", desc: "Clear paths from frontline to supervisory and control-room roles." },
  { icon: Heart, title: "Wellbeing First", desc: "Reasonable shift patterns, mental-health support and uniform provided." },
  { icon: Users, title: "Respected Team", desc: "Professional culture, no aggressive tactics — calm, capable people." },
];

export default function RecruitmentPage() {
  useSEO({
    title: "Security Jobs UK — Careers in Manned Guarding, K9 & Control Room",
    description: "Apply for SIA-licensed security officer, K9 handler and control room operator roles at USI Security. Nationwide UK security jobs with proper training, progression and a respected team culture.",
    keywords: "security jobs UK, SIA jobs, security officer jobs, K9 handler jobs UK, control room operator jobs, security supervisor jobs, careers in private security UK",
    path: "/recruitment",
  });
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/recruitment", form);
      setDone(true);
      toast.success("Application received", { description: "Thanks — a member of our team will be in touch shortly." });
      setForm({ name: "", phone: "", email: "", message: "" });
      setTimeout(() => setDone(false), 4000);
    } catch {
      toast.error("Couldn't submit", { description: "Please try again or email us directly." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="recruitment-page" className="pt-24">
      {/* Hero */}
      <section className="relative py-20 lg:py-28 border-b border-white/10">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(6,182,212,0.1),transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">◆ Recruitment · Careers</p>
            <h1 className="font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tighter">
              Join a team that's<br />
              <span className="text-gradient-cyan">setting the standard.</span>
            </h1>
          </div>
          <div className="lg:col-span-4">
            <p className="text-lg text-slate-400 leading-relaxed">
              We're always looking for capable, professional people across the UK — officers, K9 handlers, control-room operators and supervisors.
            </p>
          </div>
        </div>
      </section>

      {/* Intro statement */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-16 lg:py-20">
        <div className="card-tech rounded-2xl p-8 lg:p-12 corner-brackets relative">
          <Briefcase className="w-10 h-10 text-cyan-400 mb-5" />
          <h2 className="font-display font-bold text-white text-2xl lg:text-3xl tracking-tight mb-4">
            Enquire about careers with USI Security
          </h2>
          <p className="text-slate-300 leading-relaxed text-lg">
            If you would like to enquire about careers with us, complete the application form below — a member of our recruitment team will be in touch shortly.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="border-y border-white/10 bg-ink-900/40 py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">◆ Why USI</p>
          <h2 className="font-display font-extrabold text-white text-3xl lg:text-5xl tracking-tighter leading-tight max-w-3xl mb-12">
            A workplace built on <span className="text-gradient-cyan">respect & rigour.</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {benefits.map((b, i) => (
              <div key={b.title} data-testid={`benefit-${i}`} className="card-tech rounded-xl p-6">
                <div className="w-11 h-11 rounded-lg bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center mb-4">
                  <b.icon className="w-5 h-5 text-cyan-400" />
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">0{i + 1}</div>
                <h3 className="font-display font-bold text-white text-lg tracking-tight mb-2">{b.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">◆ Application Form</p>
            <h2 className="font-display font-extrabold text-white text-3xl lg:text-5xl tracking-tighter leading-[1.05] mb-5">
              Tell us<br />about you.
            </h2>
            <p className="text-slate-400 leading-relaxed">
              Send a quick enquiry below. Tell us a little about your background and the role you're interested in — a member of our recruitment team will be in touch shortly.
            </p>
          </div>

          <div className="lg:col-span-7">
            <form data-testid="recruitment-form" onSubmit={submit} className="card-tech rounded-2xl p-7 lg:p-10 space-y-5">
              <Field label="Full Name" testid="rec-name" required value={form.name} onChange={(v)=>setForm({...form, name:v})} />
              <Field label="Phone number" testid="rec-phone" required value={form.phone} onChange={(v)=>setForm({...form, phone:v})} />
              <Field label="Email" type="email" testid="rec-email" required value={form.email} onChange={(v)=>setForm({...form, email:v})} />
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-slate-400 mb-2">Message *</label>
                <textarea
                  data-testid="rec-message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={(e)=>setForm({...form, message: e.target.value})}
                  placeholder="Tell us about your background, SIA status, location and the role you're interested in..."
                  className="w-full bg-ink-900 border border-white/10 rounded-md px-4 py-3 text-white placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400/50 transition resize-none"
                />
              </div>

              <div className="p-4 rounded-md bg-cyan-500/5 border border-cyan-400/20 text-sm text-slate-300">
                <span className="font-medium text-white">Note:</span> please share an outline of your experience and SIA status in the message above. We'll request a full CV when we follow up.
              </div>

              <div className="flex items-center justify-between pt-1">
                <p className="text-xs text-slate-500">Your data is handled per our Privacy Policy.</p>
                <button
                  data-testid="rec-submit"
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 text-ink-950 font-bold rounded-md px-6 py-3 text-sm transition"
                >
                  {done ? (<><CheckCircle2 className="w-4 h-4" /> Sent</>) : submitting ? "Sending..." : (<>Send <Send className="w-4 h-4" /></>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}

const Field = ({ label, type = "text", value, onChange, required, testid }) => (
  <div>
    <label className="block text-xs font-mono uppercase tracking-wider text-slate-400 mb-2">{label}{required ? " *" : ""}</label>
    <input
      data-testid={testid}
      required={required}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-ink-900 border border-white/10 rounded-md px-4 py-3 text-white placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400/50 transition"
    />
  </div>
);
